"use client"

import { useState, type MouseEvent } from "react"
import Image from "next/image"
import { AnimatePresence, motion } from "framer-motion"
import { X, ZoomIn } from "lucide-react"
import { BlogImageFallback } from "@/features/blog/components/blog-image-fallback"
import { useImageFallback } from "@/shared/hooks/use-image-fallback"
import { cn } from "@/shared/lib/utils"

function ImageLightbox({
  src,
  alt,
  isOpen,
  onClose,
}: {
  src: string
  alt: string
  isOpen: boolean
  onClose: () => void
}) {
  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-50 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          aria-label="Close"
        >
          <X className="size-6" />
        </button>
        <motion.div
          className="relative max-h-[90vh] max-w-[90vw]"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export function BlogMediaImage({
  src,
  alt,
  width,
  height,
  isTall,
}: {
  src: string
  alt: string
  width: number
  height: number
  isTall: boolean
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const { status, reloadKey, onLoad, onError } = useImageFallback({
    src,
    scope: "blog-media-image",
  })

  if (status === "error") {
    return <BlogImageFallback alt={alt} />
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className="blog-media-frame group relative my-10 block w-full cursor-zoom-in overflow-hidden rounded-lg"
        aria-label="Click to zoom image"
      >
        <Image
          key={reloadKey}
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={cn(
            "w-full rounded-lg",
            isTall ? "max-h-[500px] object-contain" : "object-cover"
          )}
          onLoad={onLoad}
          onError={onError}
        />
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/15">
          <div className="rounded-full bg-black/50 p-3 opacity-0 transition-opacity group-hover:opacity-100">
            <ZoomIn className="size-5 text-white" />
          </div>
        </div>
      </button>
      <ImageLightbox
        src={src}
        alt={alt}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  )
}
